import type { QueryClient } from '@tanstack/react-query';
import type { HkTask, Room } from '../api/types';
import type { Order, OrderLine } from '../api/restaurant';

/**
 * What the screen shows for a change that is waiting to be sent. When a change is saved for later the server has not
 * heard of it, so the cached data is updated by hand to match what the person just did. The real answer replaces
 * it as soon as the change is sent and the screen refreshes.
 */

export function patchTask(qc: QueryClient, taskId: string, change: (t: HkTask) => HkTask): void {
  qc.setQueryData<HkTask[]>(['hk', 'tasks'], (old) => old?.map((t) => (t.id === taskId ? change(t) : t)));
}

const DEAD = ['VOID', 'REJECTED'];

/** Totals and the unsent count follow the lines. */
function recount(o: Order): Order {
  const live = o.lines.filter((l) => !DEAD.includes(l.status));
  return { ...o, total: live.reduce((s, l) => s + l.total, 0), unsentCount: o.lines.filter((l) => l.status === 'NEW').length };
}

export function patchOrder(qc: QueryClient, orderId: string, change: (o: Order) => Order): void {
  qc.setQueryData<Order>(['rs', 'order', orderId], (old) => (old ? recount(change(old)) : old));
}

export const withLine = (o: Order, lineId: string, change: (l: OrderLine) => OrderLine): Order => ({
  ...o,
  lines: o.lines.map((l) => (l.id === lineId ? { ...change(l), total: change(l).unitPrice * change(l).qty } : l)),
});

export function patchRoom(qc: QueryClient, roomId: string, status: Room['status']): void {
  qc.setQueryData<Room[]>(['rooms'], (old) => old?.map((r) => (r.id === roomId ? { ...r, status } : r)));
}
